import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import { Color } from '@tiptap/extension-color'
import { TextStyle } from '@tiptap/extension-text-style'
import Image from '@tiptap/extension-image'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, TextField } from "@mui/material"
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link as LinkIcon,
  Code,
  Undo,
  Redo,
  Type,
  Heading1,
  Heading2,
  Heading3,
  ImageIcon
} from "lucide-react"
import { useEffect, useRef, useState } from 'react'
import { parseImageAlign } from './tiptap-image-align'

interface TipTapEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  minHeight?: number
}

export default function TipTapEditor({ 
  value, 
  onChange, 
  placeholder = "Start typing your email content...",
  disabled = false,
  className = "",
  minHeight = 300 
}: TipTapEditorProps) {
  
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Helper function to convert File to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = error => reject(error)
    })
  }

  const editor = useEditor({
    immediatelyRender: false, // Fix SSR hydration mismatch
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        // Ensure bullet list and ordered list are enabled
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc pl-6'
          }
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal pl-6'
          }
        },
        listItem: {
          HTMLAttributes: {
            class: 'ml-0'
          }
        }
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'text-blue-600 underline hover:text-blue-800'
        }
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      TextStyle,
      Color,
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'rounded-md'
        }
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            // Store width/height as a BARE NUMBER (no "px") so they serialize to
            // width="300" / height="200" — HTML5 dimensionless attributes that
            // OUTLOOK honors. Outlook ignores CSS `style` width/height on <img>,
            // so a "300px" attribute (the old value) was treated as invalid and
            // the image rendered full size. Gmail honors numeric attributes too.
            // The in-editor node view re-adds "px" for CSS display.
            width: {
              default: null,
              parseHTML: element => {
                const raw = element.getAttribute('width')
                return raw ? String(raw).replace(/px$/i, '') : null
              },
              renderHTML: attributes => attributes.width ? { width: String(attributes.width).replace(/px$/i, '') } : {},
            },
            height: {
              default: null,
              parseHTML: element => {
                const raw = element.getAttribute('height')
                return raw ? String(raw).replace(/px$/i, '') : null
              },
              renderHTML: attributes => attributes.height ? { height: String(attributes.height).replace(/px$/i, '') } : {},
            },
            // Make the image a clickable LINK. The URL is stored on the node and
            // serialized by renderHTML below as <a href><img></a>, and parsed
            // back from a wrapping <a> so it round-trips through save/reload. The
            // email backend CID-embeds the <img> and click-tracks the <a href>.
            href: {
              default: null,
              parseHTML: element => {
                const anchor = (element as HTMLElement).closest('a')
                return (anchor && anchor.getAttribute('href')) || element.getAttribute('data-href') || null
              },
              renderHTML: () => ({}), // emitted by the node renderHTML wrapper below, never on the <img>
            },
            // Image alignment (left / center / right) driven by the toolbar's
            // alignment tool. Serialized as a wrapping <div style="text-align:…">
            // (see renderHTML) — the ONE approach that aligns the image in BOTH
            // Gmail and Outlook (text-align on a block positions the inline img;
            // margin:auto on the img alone is ignored by Outlook).
            align: {
              default: null,
              // Shared with the round-trip tests so the parse can't silently drift.
              parseHTML: element => parseImageAlign(element as HTMLElement),
              renderHTML: () => ({}), // carried by the wrapping <div> in renderHTML
            },
          }
        },
        // Wrap the <img> in an <a> when it carries an href, so the saved/sent
        // HTML is a genuine clickable image.
        renderHTML({ node, HTMLAttributes }: { node: any; HTMLAttributes: Record<string, any> }) {
          const imgAttrs = { ...(this.options?.HTMLAttributes || {}), ...HTMLAttributes }
          let el: any = ['img', imgAttrs]
          const href = node.attrs.href
          if (href) {
            el = ['a', { href, target: '_blank', rel: 'noopener noreferrer' }, el]
          }
          const align = node.attrs.align
          if (align === 'left' || align === 'center' || align === 'right') {
            // text-align on the block positions the inline image — works in
            // Gmail AND Outlook (margin:auto on <img> is ignored by Outlook).
            return ['div', { style: `text-align:${align}`, 'data-align': align }, el] as any
          }
          return el as any
        },
        addNodeView() {
          return ({ node, getPos, editor }) => {
            const container = document.createElement('div')
            container.className = 'image-resizer-container relative inline-block max-w-full'
            container.style.position = 'relative'
            container.style.display = 'inline-block'
            
            const img = document.createElement('img')
            img.src = node.attrs.src
            img.alt = node.attrs.alt || ''
            img.className = 'rounded-md max-w-full h-auto block'
            
            // attrs are stored as bare numbers (for the email width/height
            // attributes) — re-add "px" for the editor's CSS display.
            const asCss = (v: string) => (/^\d+(\.\d+)?$/.test(String(v)) ? `${v}px` : String(v))
            if (node.attrs.width) {
              img.style.width = asCss(node.attrs.width)
            }
            if (node.attrs.height) {
              img.style.height = asCss(node.attrs.height)
            }

            // Reflect alignment in the editor: block the container + push it via
            // auto margins so it visually matches the aligned email output.
            const align = node.attrs.align
            if (align === 'left' || align === 'center' || align === 'right') {
              container.style.display = 'block'
              container.style.width = 'fit-content'
              container.style.marginLeft = align === 'left' ? '0' : 'auto'
              container.style.marginRight = align === 'right' ? '0' : 'auto'
            }

            // Create resize handles
            const handles = ['nw', 'ne', 'sw', 'se'].map(position => {
              const handle = document.createElement('div')
              handle.className = `resize-handle resize-handle-${position}`
              handle.style.position = 'absolute'
              handle.style.width = '10px'
              handle.style.height = '10px'
              handle.style.background = '#3b82f6'
              handle.style.border = '2px solid white'
              handle.style.borderRadius = '50%'
              handle.style.cursor = `${position}-resize`
              handle.style.display = 'none'
              handle.style.zIndex = '10'
              
              // Position handles
              if (position === 'nw') {
                handle.style.top = '-5px'
                handle.style.left = '-5px'
              } else if (position === 'ne') {
                handle.style.top = '-5px'
                handle.style.right = '-5px'
              } else if (position === 'sw') {
                handle.style.bottom = '-5px'
                handle.style.left = '-5px'
              } else if (position === 'se') {
                handle.style.bottom = '-5px'
                handle.style.right = '-5px'
              }
              
              // Add drag functionality
              handle.addEventListener('mousedown', (e) => {
                e.preventDefault()
                e.stopPropagation()
                
                const startX = e.clientX
                const startY = e.clientY
                const startWidth = img.offsetWidth
                const startHeight = img.offsetHeight
                const aspectRatio = startWidth / startHeight
                
                const onMouseMove = (moveEvent: MouseEvent) => {
                  let newWidth = startWidth
                  let newHeight = startHeight
                  
                  if (position.includes('e')) {
                    newWidth = startWidth + (moveEvent.clientX - startX)
                  } else if (position.includes('w')) {
                    newWidth = startWidth - (moveEvent.clientX - startX)
                  }
                  
                  // Maintain aspect ratio
                  newHeight = newWidth / aspectRatio
                  
                  // Apply minimum size
                  if (newWidth < 50) {
                    newWidth = 50
                    newHeight = newWidth / aspectRatio
                  }
                  
                  img.style.width = `${newWidth}px`
                  img.style.height = `${newHeight}px`
                }
                
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove)
                  document.removeEventListener('mouseup', onMouseUp)
                  
                  // Update node attributes
                  const pos = getPos()
                  if (typeof pos === 'number') {
                    editor.commands.updateAttributes('image', {
                      // bare numbers → width="300" / height="200" (Outlook-safe)
                      width: String(Math.round(img.offsetWidth)),
                      height: String(Math.round(img.offsetHeight))
                    })
                  }
                }
                
                document.addEventListener('mousemove', onMouseMove)
                document.addEventListener('mouseup', onMouseUp)
              })
              
              return handle
            })
            
            container.appendChild(img)

            // Visual "this image links somewhere" indicator inside the editor.
            if (node.attrs.href) {
              const badge = document.createElement('div')
              badge.textContent = '🔗'
              badge.title = `Links to: ${node.attrs.href}`
              Object.assign(badge.style, {
                position: 'absolute', top: '4px', left: '4px',
                background: 'rgba(59,130,246,0.92)', color: '#ffffff',
                fontSize: '11px', lineHeight: '1', padding: '3px 5px',
                borderRadius: '4px', zIndex: '11', pointerEvents: 'none',
              })
              container.appendChild(badge)
            }

            handles.forEach(handle => container.appendChild(handle))
            
            // Show/hide handles on selection
            container.addEventListener('click', (e) => {
              e.stopPropagation()
              handles.forEach(h => h.style.display = 'block')
            })
            
            // Hide handles when clicking outside
            const hideHandles = (e: MouseEvent) => {
              if (!container.contains(e.target as Node)) {
                handles.forEach(h => h.style.display = 'none')
              }
            }
            
            setTimeout(() => {
              document.addEventListener('click', hideHandles)
            }, 0)
            
            return {
              dom: container,
              destroy: () => {
                document.removeEventListener('click', hideHandles)
              }
            }
          }
        }
      })
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      onChange(html)
    },
    editorProps: {
      handlePaste: (view, event, slice) => {
        const items = Array.from(event.clipboardData?.items || [])
        const imageItems = items.filter(item => item.type.indexOf('image') === 0)
        
        if (imageItems.length > 0) {
          event.preventDefault()
          
          imageItems.forEach(item => {
            const file = item.getAsFile()
            if (file) {
              fileToBase64(file).then(base64 => {
                // Use view to insert image
                const { schema } = view.state
                const node = schema.nodes.image.create({ src: base64 })
                const transaction = view.state.tr.replaceSelectionWith(node)
                view.dispatch(transaction)
              }).catch(error => {
                console.error('Failed to convert image:', error)
              })
            }
          })
          
          return true
        }
        
        return false
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
          const images = Array.from(event.dataTransfer.files).filter(file => 
            file.type.startsWith('image/')
          )
          
          if (images.length > 0) {
            event.preventDefault()
            
            const { schema } = view.state
            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY })
            
            images.forEach(image => {
              fileToBase64(image).then(base64 => {
                if (coordinates) {
                  const node = schema.nodes.image.create({ src: base64 })
                  const transaction = view.state.tr.insert(coordinates.pos, node)
                  view.dispatch(transaction)
                }
              }).catch(error => {
                console.error('Failed to convert dropped image:', error)
              })
            })
            
            return true
          }
        }
        
        return false
      }
    }
  })

  // Update content when value prop changes (but not during typing)
  useEffect(() => {
    if (editor) {
      const currentContent = editor.getHTML()
      // Only update if content is different and editor is not focused
      if (currentContent !== value && !editor.isFocused) {
        editor.commands.setContent(value)
      }
    }
  }, [value, editor])

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [disabled, editor])

  // Link dialog — a CUSTOM in-app dialog, never window.prompt (the app never
  // uses native browser dialogs; off-design + unstubbable in Cypress).
  // MUST be declared ABOVE the `if (!editor) return null` early return below —
  // a hook placed after an early return is conditionally called and crashes
  // React ("Rendered more hooks than during the previous render" → blank form).
  const [linkDialog, setLinkDialog] = useState<{ open: boolean; mode: 'image' | 'text'; value: string }>({
    open: false,
    mode: 'text',
    value: '',
  })

  if (!editor) {
    return null
  }

  // Toolbar button component
  const ToolbarButton = ({
    onClick,
    isActive = false,
    disabled: buttonDisabled = false,
    children,
    title
  }: {
    onClick: () => void
    isActive?: boolean
    disabled?: boolean
    children: React.ReactNode
    title: string
  }) => (
    <IconButton
      type="button"
      size="small"
      onClick={onClick}
      disabled={disabled || buttonDisabled}
      title={title}
      sx={{
        height: 32,
        width: 32,
        borderRadius: 1,
        ...(isActive
          ? { bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" } }
          : { color: "text.secondary" }),
      }}
    >
      {children}
    </IconButton>
  )

  const addLink = () => {
    // If an IMAGE is selected, the link applies to the image (renders as a
    // clickable <a href><img></a> in the saved/sent email); otherwise it links
    // the selected text.
    if (editor.isActive('image')) {
      setLinkDialog({ open: true, mode: 'image', value: editor.getAttributes('image').href || '' })
      return
    }
    setLinkDialog({ open: true, mode: 'text', value: editor.getAttributes('link').href || '' })
  }

  const applyLink = () => {
    const { mode, value } = linkDialog
    const url = value.trim()
    setLinkDialog((d) => ({ ...d, open: false }))

    if (mode === 'image') {
      editor.chain().focus().updateAttributes('image', { href: url || null }).run()
      return
    }
    if (url === '') {
      // empty → remove the link
      editor.chain().focus().unsetLink().run()
      return
    }
    const { from, to } = editor.state.selection
    if (from !== to) {
      // text selected → link it
      editor.chain().focus().setLink({ href: url }).run()
    } else {
      // nothing selected → insert the URL as a linked text
      editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run()
    }
  }

  const handleImageUpload = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      try {
        const base64 = await fileToBase64(file)
        editor?.chain().focus().setImage({ src: base64 }).run()
      } catch (error) {
        console.error('Failed to upload image:', error)
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Box
      sx={{ border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}
      {...(className ? { className } : {})}
    >
      {/* Toolbar */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", p: 1, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>

        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <Undo size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <Redo size={16} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />
        
        {/* Text Formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <Bold size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <Italic size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline"
        >
          <UnderlineIcon size={16} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

        {/* Headings */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          isActive={editor.isActive('paragraph')}
          title="Paragraph"
        >
          <Type size={16} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

        {/* Alignment — aligns the SELECTED IMAGE if one is selected, else text */}
        <ToolbarButton
          onClick={() => editor.isActive('image')
            ? editor.chain().focus().updateAttributes('image', { align: 'left' }).run()
            : editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive('image')
            ? editor.getAttributes('image').align === 'left'
            : editor.isActive({ textAlign: 'left' })}
          title={editor.isActive('image') ? 'Align image left' : 'Align Left'}
        >
          <AlignLeft size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.isActive('image')
            ? editor.chain().focus().updateAttributes('image', { align: 'center' }).run()
            : editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive('image')
            ? editor.getAttributes('image').align === 'center'
            : editor.isActive({ textAlign: 'center' })}
          title={editor.isActive('image') ? 'Center image' : 'Align Center'}
        >
          <AlignCenter size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.isActive('image')
            ? editor.chain().focus().updateAttributes('image', { align: 'right' }).run()
            : editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive('image')
            ? editor.getAttributes('image').align === 'right'
            : editor.isActive({ textAlign: 'right' })}
          title={editor.isActive('image') ? 'Align image right' : 'Align Right'}
        >
          <AlignRight size={16} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered size={16} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

        {/* Links — works on selected text AND on a selected image (makes the image clickable) */}
        <ToolbarButton
          onClick={addLink}
          isActive={editor.isActive('link') || (editor.isActive('image') && !!editor.getAttributes('image').href)}
          title={editor.isActive('image') ? 'Make image a link' : 'Insert Link'}
        >
          <LinkIcon size={16} />
        </ToolbarButton>
        
        {editor.isActive('link') && (
          <Button
            type="button"
            variant="text"
            size="small"
            color="inherit"
            onClick={() => editor.chain().focus().unsetLink().run()}
            sx={{ height: 32, px: 1, fontSize: 12, color: "text.secondary" }}
          >
            Remove Link
          </Button>
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

        {/* Image Upload */}
        <ToolbarButton
          onClick={handleImageUpload}
          title="Insert Image"
        >
          <ImageIcon size={16} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

        {/* Code Block */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          title="Code Block"
        >
          <Code size={16} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

        {/* Text Color */}
        <input
          type="color"
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          disabled={disabled}
          style={{ width: 32, height: 32, border: "1px solid rgba(0,0,0,0.23)", borderRadius: 8, cursor: "pointer", background: "transparent", padding: 0 }}
          title="Text Color"
        />
      </Box>

      {/* Editor */}
      <Box
        sx={{
          position: "relative",
          p: 2,
          "& .ProseMirror": { minHeight: 200, outline: "none" },
          "& .ProseMirror p": { my: 1 },
          "& .ProseMirror ul": { listStyleType: "disc", pl: 3, my: 1 },
          "& .ProseMirror ol": { listStyleType: "decimal", pl: 3, my: 1 },
          "& .ProseMirror li": { ml: 0, my: 0.5 },
          ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
        }}
      >
        <EditorContent
          editor={editor}
          style={{ minHeight }}
        />
        <style jsx global>{`
          .ProseMirror h1 {
            font-size: 1.875rem;
            font-weight: bold;
            margin: 1rem 0;
            line-height: 1.2;
          }
          .ProseMirror h2 {
            font-size: 1.5rem;
            font-weight: bold;
            margin: 0.75rem 0;
            line-height: 1.3;
          }
          .ProseMirror h3 {
            font-size: 1.25rem;
            font-weight: bold;
            margin: 0.5rem 0;
            line-height: 1.4;
          }
          .ProseMirror a {
            color: #2563eb;
            text-decoration: underline;
            cursor: pointer;
          }
          .ProseMirror a:hover {
            color: #1e40af;
            text-decoration: underline;
          }
          .ProseMirror ul,
          .ProseMirror ol {
            padding-left: 1.5rem;
            margin: 0.5rem 0;
          }
          .ProseMirror ul {
            list-style-type: disc;
          }
          .ProseMirror ol {
            list-style-type: decimal;
          }
          .ProseMirror li {
            margin: 0.25rem 0;
          }
          .ProseMirror li p {
            margin: 0;
          }
          .image-resizer-container:hover {
            cursor: pointer;
          }
          .image-resizer-container img {
            display: block;
          }
          .resize-handle {
            transition: opacity 0.2s;
          }
        `}</style>
      </Box>

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {/* Link dialog — custom in-app, replaces window.prompt */}
      <Dialog
        open={linkDialog.open}
        onClose={() => setLinkDialog((d) => ({ ...d, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{linkDialog.mode === 'image' ? 'Link this image' : 'Add a link'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="url"
            label="URL"
            placeholder="https://example.com"
            helperText="Leave empty to remove the link"
            value={linkDialog.value}
            onChange={(e) => setLinkDialog((d) => ({ ...d, value: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyLink()
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setLinkDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={applyLink}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

